import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, Calendar, Clock, MapPin, Users, UserCheck, Eye, BookOpen, Star, Heart, ChevronDown } from 'lucide-react';
import { Card } from '../components/ui/card';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RelatorioExecutivo = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('data');
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dateParam || '');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const res = await axios.get(`${API}/reports/executive-dates`);
        setAvailableDates(res.data || []);
        if (!selectedDate && res.data?.length > 0) {
          setSelectedDate(res.data[0].data);
        }
      } catch { /* ignore */ }
    };
    fetchDates();
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    const fetchReport = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/reports/executive/${selectedDate}`);
        setReport(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchReport();
  }, [selectedDate]);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  };

  const formatDateShort = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <p className="font-inter text-gray-500">Carregando relatório...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24 print:pb-0 print:bg-white">
      {/* Screen-only controls */}
      <div className="print:hidden bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate('/relatorios')} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-nunito font-bold text-2xl mb-3">Relatório Executivo por Encontro</h1>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1">
              <label className="text-xs text-white/70 mb-1 block">Selecione o encontro</label>
              <select data-testid="select-date" value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full rounded-xl border-0 h-10 px-3 text-sm font-inter text-gray-800 bg-white">
                {availableDates.map(d => (
                  <option key={d.data} value={d.data}>{formatDateShort(d.data)} ({d.lancamentos} lançamentos)</option>
                ))}
              </select>
            </div>
            <button onClick={handlePrint} data-testid="print-btn"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all">
              <Printer className="w-4 h-4" /> Imprimir / PDF
            </button>
          </div>
        </div>
      </div>

      {/* Printable A4 Content */}
      {report && (
        <div className="max-w-4xl mx-auto px-4 py-6 print:px-0 print:py-0 print:max-w-none" data-testid="executive-report">
          <div className="bg-white rounded-2xl shadow-lg border border-[#E1ECE5] overflow-hidden print:shadow-none print:border-0 print:rounded-none">

            {/* ====== HEADER ====== */}
            <div className="bg-[#1A4D2E] text-white p-6 print:p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img src="/logos/logo_raizes_white.png" alt="Raízes" className="h-14 print:h-10 object-contain" />
                <div className="border-l border-white/30 pl-4">
                  <h2 className="font-nunito font-bold text-xl print:text-lg">Relatório Executivo</h2>
                  <p className="text-white/80 text-sm capitalize">{formatDate(report.data)}</p>
                </div>
              </div>
              <img src="/logos/logo_semente.png" alt="Sementes" className="h-12 print:h-8 object-contain" />
            </div>

            {/* ====== INFO BAR ====== */}
            <div className="bg-[#F2F7F4] px-6 py-3 print:px-4 print:py-2 flex flex-wrap gap-4 text-sm border-b border-[#E1ECE5]">
              <span className="flex items-center gap-1.5 text-[#1A4D2E] font-semibold">
                <Calendar className="w-4 h-4" /> {formatDateShort(report.data)}
              </span>
              <span className="flex items-center gap-1.5 text-[#1A4D2E]">
                <Clock className="w-4 h-4" /> {report.horario_inicio} - {report.horario_fim}
              </span>
              {report.ambiente && (
                <span className="flex items-center gap-1.5 text-[#1A4D2E]">
                  <MapPin className="w-4 h-4" /> {report.ambiente}
                </span>
              )}
              {report.tema && (
                <span className="flex items-center gap-1.5 text-[#1A4D2E]">
                  <BookOpen className="w-4 h-4" /> {report.tema}
                </span>
              )}
            </div>

            <div className="p-6 print:p-4 space-y-6 print:space-y-4">

              {/* ====== RESUMO GERAL ====== */}
              <div className="grid grid-cols-4 gap-3 print:gap-2">
                <div className="bg-[#1A4D2E] text-white rounded-xl p-4 print:p-2 text-center print:rounded-lg">
                  <p className="text-xs text-white/70">Cadastradas</p>
                  <p className="font-nunito font-bold text-3xl print:text-2xl">{report.total_cadastradas}</p>
                </div>
                <div className="bg-[#2D8B5A] text-white rounded-xl p-4 print:p-2 text-center print:rounded-lg">
                  <p className="text-xs text-white/70">Presentes</p>
                  <p className="font-nunito font-bold text-3xl print:text-2xl">{report.presentes.total}</p>
                </div>
                <div className="bg-[#F4D35E] text-[#1A4D2E] rounded-xl p-4 print:p-2 text-center print:rounded-lg">
                  <p className="text-xs text-[#1A4D2E]/70">Ausentes</p>
                  <p className="font-nunito font-bold text-3xl print:text-2xl">{report.total_cadastradas - report.presentes.total}</p>
                </div>
                <div className="bg-blue-500 text-white rounded-xl p-4 print:p-2 text-center print:rounded-lg">
                  <p className="text-xs text-white/70">Visitantes</p>
                  <p className="font-nunito font-bold text-3xl print:text-2xl">{report.visitantes.total}</p>
                </div>
              </div>

              {/* ====== PRESENTES ====== */}
              <div>
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-3 flex items-center gap-2 border-b border-[#E1ECE5] pb-2">
                  <UserCheck className="w-5 h-5" /> Crianças Presentes
                </h3>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-blue-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-blue-600 font-semibold">Meninos</p>
                    <p className="font-nunito font-bold text-2xl text-blue-700">{report.presentes.meninos}</p>
                  </div>
                  <div className="bg-pink-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-pink-600 font-semibold">Meninas</p>
                    <p className="font-nunito font-bold text-2xl text-pink-700">{report.presentes.meninas}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-green-600 font-semibold">Total</p>
                    <p className="font-nunito font-bold text-2xl text-green-700">{report.presentes.total}</p>
                  </div>
                </div>

                {report.presentes.por_idade.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-testid="table-presentes-idade">
                      <thead>
                        <tr className="bg-[#F2F7F4] text-[#1A4D2E]">
                          <th className="px-4 py-2 text-left font-semibold border-b">Idade</th>
                          <th className="px-4 py-2 text-center font-semibold border-b">Meninos</th>
                          <th className="px-4 py-2 text-center font-semibold border-b">Meninas</th>
                          <th className="px-4 py-2 text-center font-semibold border-b">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.presentes.por_idade.map((item) => (
                          <tr key={item.idade} className="border-b border-gray-100">
                            <td className="px-4 py-2 font-semibold text-[#1A4D2E]">{item.idade} anos</td>
                            <td className="px-4 py-2 text-center text-blue-600 font-semibold">{item.meninos}</td>
                            <td className="px-4 py-2 text-center text-pink-600 font-semibold">{item.meninas}</td>
                            <td className="px-4 py-2 text-center font-bold">{item.total}</td>
                          </tr>
                        ))}
                        <tr className="bg-[#F2F7F4] font-bold">
                          <td className="px-4 py-2 text-[#1A4D2E]">Total</td>
                          <td className="px-4 py-2 text-center text-blue-700">{report.presentes.meninos}</td>
                          <td className="px-4 py-2 text-center text-pink-700">{report.presentes.meninas}</td>
                          <td className="px-4 py-2 text-center text-[#1A4D2E]">{report.presentes.total}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ====== VISITANTES ====== */}
              <div>
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-3 flex items-center gap-2 border-b border-[#E1ECE5] pb-2">
                  <Eye className="w-5 h-5" /> Visitantes
                </h3>
                {report.visitantes.total > 0 ? (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="bg-blue-50 rounded-xl p-3 text-center">
                        <p className="text-xs text-blue-600 font-semibold">Meninos</p>
                        <p className="font-nunito font-bold text-2xl text-blue-700">{report.visitantes.meninos}</p>
                      </div>
                      <div className="bg-pink-50 rounded-xl p-3 text-center">
                        <p className="text-xs text-pink-600 font-semibold">Meninas</p>
                        <p className="font-nunito font-bold text-2xl text-pink-700">{report.visitantes.meninas}</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-3 text-center">
                        <p className="text-xs text-green-600 font-semibold">Total</p>
                        <p className="font-nunito font-bold text-2xl text-green-700">{report.visitantes.total}</p>
                      </div>
                    </div>

                    {report.visitantes.por_idade.length > 0 && (
                      <div className="overflow-x-auto mb-3">
                        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-testid="table-visitantes-idade">
                          <thead>
                            <tr className="bg-blue-50 text-blue-800">
                              <th className="px-4 py-2 text-left font-semibold border-b">Idade</th>
                              <th className="px-4 py-2 text-center font-semibold border-b">Meninos</th>
                              <th className="px-4 py-2 text-center font-semibold border-b">Meninas</th>
                              <th className="px-4 py-2 text-center font-semibold border-b">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {report.visitantes.por_idade.map((item) => (
                              <tr key={item.idade} className="border-b border-gray-100">
                                <td className="px-4 py-2 font-semibold">{item.idade} anos</td>
                                <td className="px-4 py-2 text-center text-blue-600 font-semibold">{item.meninos}</td>
                                <td className="px-4 py-2 text-center text-pink-600 font-semibold">{item.meninas}</td>
                                <td className="px-4 py-2 text-center font-bold">{item.total}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-testid="table-visitantes-lista">
                        <thead>
                          <tr className="bg-gray-50 text-gray-700">
                            <th className="px-4 py-2 text-left font-semibold border-b">Nome</th>
                            <th className="px-4 py-2 text-center font-semibold border-b">Sexo</th>
                            <th className="px-4 py-2 text-center font-semibold border-b">Idade</th>
                            <th className="px-4 py-2 text-left font-semibold border-b">Convidado por</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.visitantes.lista.map((v, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              <td className="px-4 py-2 font-semibold text-[#1A4D2E]">{v.nome}</td>
                              <td className="px-4 py-2 text-center">
                                {v.sexo === 'M' && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded font-semibold">M</span>}
                                {v.sexo === 'F' && <span className="text-xs bg-pink-100 text-pink-600 px-2 py-0.5 rounded font-semibold">F</span>}
                                {!v.sexo && <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-4 py-2 text-center">{v.idade ?? '—'}</td>
                              <td className="px-4 py-2 text-gray-600">{v.convidado_por || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4">Nenhum visitante registrado neste encontro</p>
                )}
              </div>

              {/* ====== INDICES ====== */}
              <div>
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-3 flex items-center gap-2 border-b border-[#E1ECE5] pb-2">
                  <Star className="w-5 h-5" /> Índices do Encontro
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-testid="table-indices">
                    <thead>
                      <tr className="bg-[#1A4D2E] text-white">
                        <th className="px-4 py-3 text-left font-semibold">Indicador</th>
                        <th className="px-4 py-3 text-center font-semibold">Quantidade</th>
                        <th className="px-4 py-3 text-center font-semibold">Base</th>
                        <th className="px-4 py-3 text-center font-semibold">Percentual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Presença', icon: '✓', data: report.indices.presenca, desc: 'em relação ao total de cadastradas' },
                        { label: 'Pontualidade', icon: '⏰', data: report.indices.pontualidade, desc: 'em relação aos presentes' },
                        { label: 'Bíblia', icon: '📖', data: report.indices.biblia, desc: 'em relação aos presentes' },
                        { label: 'Versículo', icon: '📝', data: report.indices.versiculo, desc: 'em relação aos presentes' },
                        { label: 'Devocional', icon: '🙏', data: report.indices.devocional, desc: 'da semana, em relação aos presentes' },
                      ].map((item, i) => (
                        <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-base">{item.icon}</span>
                              <div>
                                <p className="font-semibold text-[#1A4D2E]">{item.label}</p>
                                <p className="text-[10px] text-gray-400">{item.desc}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center font-bold text-lg text-[#1A4D2E]">{item.data.valor}</td>
                          <td className="px-4 py-3 text-center text-gray-500">de {item.data.total}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-bold text-lg ${item.data.percentual >= 75 ? 'text-green-600' : item.data.percentual >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                              {item.data.percentual}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Visual bars */}
                <div className="mt-4 space-y-2">
                  {[
                    { label: 'Presença', pct: report.indices.presenca.percentual, color: 'bg-[#2D8B5A]' },
                    { label: 'Pontualidade', pct: report.indices.pontualidade.percentual, color: 'bg-blue-500' },
                    { label: 'Bíblia', pct: report.indices.biblia.percentual, color: 'bg-purple-500' },
                    { label: 'Versículo', pct: report.indices.versiculo.percentual, color: 'bg-amber-500' },
                    { label: 'Devocional', pct: report.indices.devocional.percentual, color: 'bg-teal-500' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-gray-600 w-24 text-right">{item.label}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-5">
                        <div className={`h-5 rounded-full ${item.color} flex items-center justify-end pr-2 transition-all`}
                          style={{ width: `${Math.max(item.pct, 3)}%` }}>
                          <span className="text-[10px] font-bold text-white">{item.pct}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ====== FOOTER ====== */}
              <div className="border-t border-[#E1ECE5] pt-4 flex flex-col items-center gap-1 text-xs text-gray-400 print:pt-2">
                <div className="flex items-center justify-between w-full">
                  <p>Ministério Raízes — Projeto Sementes</p>
                  <p>Gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <p className="text-[10px] text-gray-300 mt-1">Made by @KingdomCloud - Conectando Igrejas e Ministérios</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!report && !loading && (
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="font-inter text-gray-500">Selecione um encontro para gerar o relatório</p>
        </div>
      )}

      <Navigation active="relatorios" />

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          nav, .print\\:hidden, [class*="Navigation"] { display: none !important; }
          .print\\:pb-0 { padding-bottom: 0 !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-0 { border: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
          .print\\:p-4 { padding: 1rem !important; }
          .print\\:p-2 { padding: 0.5rem !important; }
          .print\\:px-4 { padding-left: 1rem !important; padding-right: 1rem !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:py-2 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: 1rem !important; }
          .print\\:text-lg { font-size: 1.125rem !important; }
          .print\\:text-2xl { font-size: 1.5rem !important; }
          .print\\:h-10 { height: 2.5rem !important; }
          .print\\:h-8 { height: 2rem !important; }
          .print\\:gap-2 > :not([hidden]) ~ :not([hidden]) { gap: 0.5rem !important; }
          .print\\:rounded-lg { border-radius: 0.5rem !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>
    </div>
  );
};

export default RelatorioExecutivo;
